/**
 * React hook that is used to mark the block wrapper element.
 * It provides all the necessary props like the class name.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/packages/packages-block-editor/#useblockprops
 */
import { useEffect, useState, useCallback } from '@wordpress/element';

/**
 * Lets webpack process CSS, SASS or SCSS files referenced in JavaScript files.
 * Those files can contain any CSS code that gets applied to the editor.
 *
 * @see https://www.npmjs.com/package/@wordpress/scripts#using-css
 */
import { fetchData } from '../api';
import { Spinner } from '@wordpress/components';
import Pagination from './Pagination.component';
import BSHelper from '../BSHelper';
import { sprintf } from '@wordpress/i18n';

/**
 * The edit function describes the structure of your block in the context of the
 * editor. This represents what the editor will render when the block is used.
 *
 * @see https://developer.wordpress.org/block-editor/reference-guides/block-api/block-edit-save/#edit
 *
 * @return {Element} Element to render.
 */
export default function NewsFeed({ phrases, itemsPerPage = 8, order, orderBy }) {
	const [newsFeeds, setNewsFeeds] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [currentPage, setCurrentPage] = useState(1);
	const [totalPages, setTotalPages] = useState(1);
	const arrowClassIcon =
		" group-hover:translate-x-1 rtl:group-hover:-translate-x-1 transition-all " +
		(BSHelper.isPageRTL() ? "fa-arrow-left-long" : "fa-arrow-right-long");
	const numberOfCols = bseitEditorVars.themeName === 'hheo-template-2' ? 'xl:grid-cols-2' : 'xl:grid-cols-3';

	// const fetchNewsFeeds = useCallback(async (page = 1) => {
	// 	setLoading(true);
	// 	setError(null);

	// 	try {
	// 		// console.log(`Fetching newsFeeds for page ${page}`);
	// 		const response = await fetchData(`/bseitwp/v1/newsfeeds?options[paginate]=1&options[limit]=${itemsPerPage}&options[page]=${page}&options[order]=${order}&options[orderby]=${orderBy}`);
	// 		// console.log("API Response:", response);
	// 		setNewsFeeds(response.data.data);
	// 		setTotalPages(response.data.pagination.total_pages);
	// 	} catch (error) {
	// 		setError(error);
	// 	} finally {
	// 		setLoading(false);
	// 	}
	// }, [itemsPerPage]);


	const buildNewsfeedsUrl = (page) => {
		const params = new URLSearchParams({
			"options[paginate]": "1",
			"options[limit]": String(itemsPerPage),
			"options[page]": String(page),
		});

		if (order) {
			params.append("options[order]", order);
		}

		if (orderBy) {
			params.append("options[orderby]", orderBy);
		}

		return `/bseitwp/v1/newsfeeds?${params.toString()}`;
	};

	const fetchNewsFeeds = useCallback(
		async (page = 1) => {
			setLoading(true);
			setError(null);

			try {
				const response = await fetchData(buildNewsfeedsUrl(page));
				const data = response?.data?.data ?? [];
				const total = response?.data?.pagination?.total_pages ?? 1;
				setNewsFeeds(data);
				setTotalPages(total);
			} catch (error) {
				setError(error);
			} finally {
				setLoading(false);
			}
		},
		[itemsPerPage, order, orderBy]
	);


	useEffect(() => {
		// console.log(BSHelper.isPageRTL());
	});

	useEffect(() => {
		fetchNewsFeeds(currentPage);
	}, [
		fetchNewsFeeds,
		currentPage,
	]);

	const handlePageChange = (page) => {
		setCurrentPage(page);
	};

	return (
		<>
			<div className={`grid gap-5 md:px-5 lg:px-0 lg:grid-cols-2 ${numberOfCols}`}>
				{loading && (
					<div className="flex items-center justify-center w-full">
						<Spinner
							style={{
								height: "calc(2px * 20)",
								width: "calc(2px * 20)",
							}}
						/>
					</div>
				)}

				{error && <p>{error.message}</p>}

				{!loading && newsFeeds.length > 0 && newsFeeds.map((item, index) => (<div
					key={index}
					className="pb-5 overflow-hidden border rounded-lg card bg-[#f3f5f6] border-[#f3f5f6]"
				>
					<a href={item.link ?? '#'}>
						<img
							className="object-cover w-full lg:h-80"
							src={item.thumbnail}
							alt={item.title}
							loading="lazy"
						/>
					</a>
					<div className="p-5">
						<p className="gap-4 text-[#909da2] md:flex">

							{bseitEditorVars.themeName === 'hheo-template-1' && (
								<span className="flex items-center gap-1">
									<svg
										width="18"
										height="19"
										viewBox="0 0 18 19"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
									>
										<circle cx="9" cy="9.78125" r="6.5" stroke="#909DA2" />
										<path
											d="M12.375 9.78125H9.15625C9.06996 9.78125 9 9.71129 9 9.625V7.15625"
											stroke="#909DA2"
											strokeLinecap="round"
										/>
									</svg>
									<p className="text-xs font-medium leading-6 text-[#909da2] sm:text-base ms-2 text-nowrap">
										{sprintf(/* translators: %s: number of minutes */
											phrases.minutes_of_reading, BSHelper.getReadingTime(item.content))}
									</p>
								</span>
							)}

							{bseitEditorVars.themeName !== 'hheo-template-1' && (
								<span className="flex items-center gap-1">
									<svg
										width="24"
										height="24"
										viewBox="0 0 24 24"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
									>
										<path
											d="M12 2.25C7.175 2.25 3.25 6.175 3.25 11C3.25 16.118 7.94699 19.2199 11.055 21.2729L11.584 21.624C11.71 21.708 11.855 21.75 12 21.75C12.145 21.75 12.29 21.708 12.416 21.624L12.945 21.2729C16.053 19.2199 20.75 16.118 20.75 11C20.75 6.175 16.825 2.25 12 2.25ZM12.119 20.021L12 20.1001L11.881 20.021C8.871 18.033 4.75 15.311 4.75 11C4.75 7.002 8.002 3.75 12 3.75C15.998 3.75 19.25 7.002 19.25 11C19.25 15.311 15.128 18.034 12.119 20.021ZM12 7.75C10.208 7.75 8.75 9.208 8.75 11C8.75 12.792 10.208 14.25 12 14.25C13.792 14.25 15.25 12.792 15.25 11C15.25 9.208 13.792 7.75 12 7.75ZM12 12.75C11.035 12.75 10.25 11.965 10.25 11C10.25 10.035 11.035 9.25 12 9.25C12.965 9.25 13.75 10.035 13.75 11C13.75 11.965 12.965 12.75 12 12.75Z"
											fill="#909DA2"
										/>
									</svg>

									{item.meta_data.location}
								</span>
							)}

							<span className="flex items-center gap-1">
								<svg
									width="18"
									height="18"
									viewBox="0 0 18 18"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
								>
									<rect
										x="2.25"
										y="4.5"
										width="13.5"
										height="11.25"
										rx="1.25"
										stroke="#909DA2"
									/>
									<path
										d="M3 8.25H15"
										stroke="#909DA2"
										strokeLinecap="round"
									/>
									<path
										d="M6.75 12H11.25"
										stroke="#909DA2"
										strokeLinecap="round"
									/>
									<path
										d="M6 2.25L6 5.25"
										stroke="#909DA2"
										strokeLinecap="round"
									/>
									<path
										d="M12 2.25L12 5.25"
										stroke="#909DA2"
										strokeLinecap="round"
									/>
								</svg>
								{BSHelper.isPageRTL() ? BSHelper.formatDate(new Date(item.created_date), 'ar-EG') : BSHelper.formatDate(new Date(item.created_date))}
							</span>
						</p>
						<div className="grid lg:grid-cols-6">
							<div className="lg:col-span-5">
								<h2 className="pt-5 text-2xl font-bold text-body">
									<a href={item.link ?? '#'}>{BSHelper.truncate(item.title, 60)}</a>
								</h2>
							</div>
						</div>
						<p className="pt-5">
							<a
								href={item.link ?? '#'}
								className="inline-flex items-center gap-5 transition text-secondary group"
							>
								<span>{phrases.read_more}</span>
								<i className={`fa-solid ${arrowClassIcon}`}></i>
							</a>
						</p>
					</div>
				</div>))}
			</div>
			{!loading && totalPages > 1 && (<Pagination
				currentPage={currentPage}
				totalPages={totalPages}
				onPageChange={handlePageChange}
			/>)}
		</>);
}
